/**
 * Review Detail Page
 *
 * Full review interface for a single assignment.
 * Supports two modes:
 *   - Legacy mode: paper_maps + grading_drafts (teacher-uploaded scans)
 *   - Submission mode: student_submissions.grading_result JSON (online submissions)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Stack,
  LinearProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack as BackIcon,
  CheckCircle as ApproveIcon,
  Warning as WarningIcon,
  Flag as FlagIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';
import { PaperMapViewer } from '@/components/PaperMapViewer';
import { TranscriptEditor } from '@/components/TranscriptEditor';
import { GradingReviewer } from '@/components/GradingReviewer';
import { VerifierFlagsPanel } from '@/components/VerifierFlagsPanel';

// ─── Shared interfaces ────────────────────────────────────────────────────────

interface AssignmentDetails {
  id: string;
  student_name: string | null;
  student_class: string | null;
  grade_level: string;
  image_paths: string[];
  status: string;
}

interface PaperMap {
  id: string;
  version: number;
  confidence: number;
  page_count: number;
  paper_map_regions: Region[];
}

interface Region {
  id: string;
  question_id: string;
  question_label: string | null;
  page_index: number;
  bbox_x: number;
  bbox_y: number;
  bbox_width: number;
  bbox_height: number;
  confidence: number;
}

interface Transcript {
  id: string;
  question_id: string;
  text: string;
  confidence: number;
  quality_flags: string[];
  human_edited: boolean;
  version: number;
}

interface GradingDraft {
  id: string;
  question_id: string;
  marks_awarded: number;
  max_marks: number;
  confidence: number;
  rationale: string;
  student_feedback: string;
  version: number;
}

interface VerificationResult {
  id: string;
  grading_draft_id: string;
  valid: boolean;
  flags: string;
  error_count: number;
  warning_count: number;
  requires_review: boolean;
  review_priority: string;
}

// ─── Submission-mode interfaces ───────────────────────────────────────────────

interface SubmissionQuestionResult {
  questionId: string;
  marksAwarded: number;
  maxMarks: number;
  transcribedAnswer: string;
  feedback: string;
  confidence: number;
  needs_review?: boolean;
  teacher_score?: number | null;
  teacher_feedback?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  rationale?: { criterion: string; marksAwarded: number; maxMarks: number; evidence: string }[];
}

interface SubmissionGradingResult {
  questions: SubmissionQuestionResult[];
  totalMarks: number;
  maxTotalMarks: number;
  gradedAt: string;
}

interface StudentSubmission {
  id: string;
  student_name: string | null;
  student_class: string | null;
  status: string;
  submitted_at: string;
  grading_result: SubmissionGradingResult | string | null;
}

// ─── Review flag ──────────────────────────────────────────────────────────────

type ReviewFlag = {
  type: 'verification' | 'low_grading_confidence' | 'low_transcript_confidence' | 'needs_review';
  label: string;
  severity: 'error' | 'warning' | 'info';
};

const GRADING_CONFIDENCE_THRESHOLD = 0.7;
const TRANSCRIPT_CONFIDENCE_THRESHOLD = 0.8;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Safely parse grading_result whether it arrived as a JSON string or an object.
 * Returns null on parse failure instead of throwing.
 */
function parseGradingResult(raw: SubmissionGradingResult | string | null): SubmissionGradingResult | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw;
}

/**
 * Build a GradingDraft map from a StudentSubmission.
 * Parses grading_result internally; returns {} on any failure.
 */
function submissionToDrafts(submission: StudentSubmission): Record<string, GradingDraft> {
  try {
    const gr = parseGradingResult(submission.grading_result);
    if (!gr || !Array.isArray(gr.questions)) return {};
    const map: Record<string, GradingDraft> = {};
    gr.questions.forEach((q) => {
      map[q.questionId] = {
        id: `${submission.id}-${q.questionId}`,
        question_id: q.questionId,
        marks_awarded: q.teacher_score != null ? q.teacher_score : q.marksAwarded,
        max_marks: q.maxMarks,
        confidence: q.confidence ?? 1,
        rationale: q.rationale?.map((r) => r.evidence).join('\n') ?? '',
        student_feedback: q.teacher_feedback ?? q.feedback ?? '',
        version: 1,
      };
    });
    return map;
  } catch (e) {
    console.error('submissionToDrafts: failed to parse grading_result for', submission.id, e);
    return {};
  }
}

/**
 * Build a Transcript map from a StudentSubmission.
 * Parses grading_result internally; returns {} on any failure.
 */
function submissionToTranscripts(submission: StudentSubmission): Record<string, Transcript> {
  try {
    const gr = parseGradingResult(submission.grading_result);
    if (!gr || !Array.isArray(gr.questions)) return {};
    const map: Record<string, Transcript> = {};
    gr.questions.forEach((q) => {
      map[q.questionId] = {
        id: q.questionId,
        question_id: q.questionId,
        text: q.transcribedAnswer ?? '',
        confidence: 1,
        quality_flags: [],
        human_edited: false,
        version: 1,
      };
    });
    return map;
  } catch (e) {
    console.error('submissionToTranscripts: failed to parse grading_result for', submission.id, e);
    return {};
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ReviewDetailPage() {
  const { id: assignmentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);

  // Legacy mode state
  const [assignment, setAssignment] = useState<AssignmentDetails | null>(null);
  const [paperMap, setPaperMap] = useState<PaperMap | null>(null);
  const [transcripts, setTranscripts] = useState<Record<string, Transcript>>({});
  const [gradingDrafts, setGradingDrafts] = useState<Record<string, GradingDraft>>({});
  const [verifications, setVerifications] = useState<Record<string, VerificationResult>>({});
  const [imageUrls, setImageUrls] = useState<string[]>([]);

  // Submission mode state
  const [isSubmissionMode, setIsSubmissionMode] = useState(false);
  const [submissions, setSubmissions] = useState<StudentSubmission[]>([]);
  const [selectedSubmissionIndex, setSelectedSubmissionIndex] = useState(0);

  // UI state
  const [confirmedQuestions, setConfirmedQuestions] = useState<Set<string>>(new Set());
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  // ── Derived: question list ──────────────────────────────────────────────────

  const allQuestions = useMemo<string[]>(() => {
    if (isSubmissionMode) {
      const sub = submissions[selectedSubmissionIndex];
      if (!sub) return [];
      const gr = parseGradingResult(sub.grading_result);
      return gr?.questions.map((q) => q.questionId) ?? [];
    }
    return paperMap?.paper_map_regions?.map((r) => r.question_id) ?? [];
  }, [isSubmissionMode, submissions, selectedSubmissionIndex, paperMap]);

  // ── Derived: flags per question ────────────────────────────────────────────

  const questionFlags = useMemo(() => {
    const flags: Record<string, ReviewFlag[]> = {};

    if (isSubmissionMode) {
      const sub = submissions[selectedSubmissionIndex];
      if (!sub) return flags;
      const gr = parseGradingResult(sub.grading_result);
      if (!gr) return flags;

      gr.questions.forEach((q) => {
        const qFlags: ReviewFlag[] = [];
        if (q.needs_review === true && q.reviewed_at == null) {
          qFlags.push({ type: 'needs_review', label: '標記為需要審核', severity: 'warning' });
        }
        if ((q.confidence ?? 1) < GRADING_CONFIDENCE_THRESHOLD) {
          qFlags.push({
            type: 'low_grading_confidence',
            label: `評分信心度低 (${Math.round((q.confidence ?? 0) * 100)}%)`,
            severity: (q.confidence ?? 1) < 0.5 ? 'error' : 'warning',
          });
        }
        flags[q.questionId] = qFlags;
      });
      return flags;
    }

    // Legacy mode
    const regions = paperMap?.paper_map_regions ?? [];
    for (const region of regions) {
      const qId = region.question_id;
      const qFlags: ReviewFlag[] = [];

      const v = verifications[qId];
      if (v?.requires_review) {
        qFlags.push({
          type: 'verification',
          label: `驗證問題 (${v.error_count} 錯誤, ${v.warning_count} 警告)`,
          severity: v.error_count > 0 ? 'error' : 'warning',
        });
      }

      const draft = gradingDrafts[qId];
      if (draft && draft.confidence < GRADING_CONFIDENCE_THRESHOLD) {
        qFlags.push({
          type: 'low_grading_confidence',
          label: `評分信心度低 (${Math.round(draft.confidence * 100)}%)`,
          severity: draft.confidence < 0.5 ? 'error' : 'warning',
        });
      }

      const t = transcripts[qId];
      if (t && t.confidence < TRANSCRIPT_CONFIDENCE_THRESHOLD) {
        qFlags.push({
          type: 'low_transcript_confidence',
          label: `轉錄信心度低 (${Math.round(t.confidence * 100)}%)`,
          severity: t.confidence < 0.5 ? 'error' : 'warning',
        });
      }

      flags[qId] = qFlags;
    }
    return flags;
  }, [isSubmissionMode, submissions, selectedSubmissionIndex, paperMap, verifications, gradingDrafts, transcripts]);

  const needsReview = useCallback(
    (questionId: string) => (questionFlags[questionId]?.length ?? 0) > 0,
    [questionFlags],
  );

  const flaggedQuestions = useMemo(
    () => allQuestions.filter((q) => needsReview(q)),
    [allQuestions, needsReview],
  );

  const reviewedCount = useMemo(
    () => flaggedQuestions.filter((q) => confirmedQuestions.has(q)).length,
    [flaggedQuestions, confirmedQuestions],
  );

  const allFlaggedReviewed = flaggedQuestions.length > 0 && reviewedCount === flaggedQuestions.length;

  const visibleQuestions = useMemo(
    () => (showFlaggedOnly ? flaggedQuestions : allQuestions),
    [showFlaggedOnly, flaggedQuestions, allQuestions],
  );

  // ── Derived: display name ──────────────────────────────────────────────────

  const displayStudentName = useMemo(() => {
    if (isSubmissionMode) {
      return submissions[selectedSubmissionIndex]?.student_name ?? '未知學生';
    }
    return assignment?.student_name ?? '未知學生';
  }, [isSubmissionMode, submissions, selectedSubmissionIndex, assignment]);

  // ── Sync drafts/transcripts when switching submission ──────────────────────
  //
  // Only depends on selectedSubmissionIndex (and isSubmissionMode for the initial
  // transition). We intentionally exclude `submissions` from deps: per-question
  // overrides update submissions in place via handleGradingOverride and should NOT
  // trigger a full UI reset (confirmed questions, selected question, selected tab).
  // gradingDrafts is kept in sync inline by handleGradingOverride instead.
  //
   
  useEffect(() => {
    if (!isSubmissionMode) return;
    const sub = submissions[selectedSubmissionIndex];
    if (!sub) return;

    setGradingDrafts(submissionToDrafts(sub));
    setTranscripts(submissionToTranscripts(sub));
    setConfirmedQuestions(new Set());

    const gr = parseGradingResult(sub.grading_result);
    setSelectedQuestion(gr?.questions[0]?.questionId ?? null);
    setSelectedTab(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmissionMode, selectedSubmissionIndex]);

  // ── Data loading ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (assignmentId && user) loadAssignmentData();
  }, [assignmentId, user]);

  const loadAssignmentData = async () => {
    if (!assignmentId) return;
    setLoading(true);
    setError(null);

    try {
      const { data: assignmentData, error: assignmentError } = await supabase
        .from('assignments')
        .select('*')
        .eq('id', assignmentId)
        .single();

      if (assignmentError) throw assignmentError;
      setAssignment(assignmentData);

      // Try paper_maps first (legacy mode)
      const { data: paperMapData } = await supabase
        .from('paper_maps')
        .select('*, paper_map_regions (*)')
        .eq('assignment_id', assignmentId)
        .eq('is_current', true)
        .single();

      if (paperMapData) {
        // ── Legacy mode ──
        setPaperMap(paperMapData);

        if (paperMapData.paper_map_regions?.length > 0) {
          setSelectedQuestion(paperMapData.paper_map_regions[0].question_id);
        }

        const urls = await Promise.all(
          (assignmentData.image_paths || []).map(async (path: string) => {
            const { data } = supabase.storage.from('assignments').getPublicUrl(path);
            return data.publicUrl;
          }),
        );
        setImageUrls(urls);

        const { data: transcriptData } = await supabase
          .from('transcripts')
          .select('*')
          .eq('paper_map_id', paperMapData.id)
          .eq('is_current', true);

        if (transcriptData) {
          const transcriptMap: Record<string, Transcript> = {};
          transcriptData.forEach((t) => { transcriptMap[t.question_id] = t; });
          setTranscripts(transcriptMap);
        }

        const { data: draftData } = await supabase
          .from('grading_drafts')
          .select('*')
          .eq('assignment_id', assignmentId)
          .eq('is_current', true);

        if (draftData) {
          const draftMap: Record<string, GradingDraft> = {};
          draftData.forEach((d) => { draftMap[d.question_id] = d; });
          setGradingDrafts(draftMap);

          const draftIds = draftData.map((d) => d.id);
          if (draftIds.length > 0) {
            const { data: verificationData } = await supabase
              .from('verification_results')
              .select('*')
              .in('grading_draft_id', draftIds);

            if (verificationData) {
              const verificationMap: Record<string, VerificationResult> = {};
              verificationData.forEach((v) => {
                const draft = draftData.find((d) => d.id === v.grading_draft_id);
                if (draft) verificationMap[draft.question_id] = v;
              });
              setVerifications(verificationMap);
            }
          }
        }
      } else {
        // ── Submission mode ──
        const { data: submissionData } = await supabase
          .from('student_submissions')
          .select('*')
          .eq('assignment_id', assignmentId)
          .eq('status', 'graded')
          .order('student_name');

        // Filter out submissions with missing or unparseable grading_result
        const validSubmissions = (submissionData || []).filter((s) => {
          try {
            const gr = typeof s.grading_result === 'string'
              ? JSON.parse(s.grading_result)
              : s.grading_result;
            return gr && Array.isArray(gr.questions) && gr.questions.length > 0;
          } catch {
            return false;
          }
        });

        setIsSubmissionMode(true);
        setSubmissions(validSubmissions);
        setSelectedSubmissionIndex(0);
      }
    } catch (err) {
      console.error('Error loading assignment data:', err);
      setError('無法載入作業資料');
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleConfirmQuestion = useCallback(
    (questionId: string) => {
      setConfirmedQuestions((prev) => {
        const next = new Set(prev);
        next.add(questionId);
        return next;
      });

      const nextUnconfirmed = flaggedQuestions.find(
        (q) => q !== questionId && !confirmedQuestions.has(q),
      );
      if (nextUnconfirmed) setSelectedQuestion(nextUnconfirmed);
    },
    [flaggedQuestions, confirmedQuestions],
  );

  const handleTranscriptUpdate = useCallback(
    async (questionId: string, newText: string) => {
      const transcript = transcripts[questionId];
      if (!transcript) return;

      setSaving(true);
      try {
        if (!isSubmissionMode) {
          const { error } = await supabase
            .from('transcripts')
            .update({
              text: newText,
              human_edited: true,
              edited_by: user?.id,
              edited_at: new Date().toISOString(),
              original_text: transcript.text,
              confidence: 1.0,
            })
            .eq('id', transcript.id);
          if (error) throw error;
        }

        setTranscripts((prev) => ({
          ...prev,
          [questionId]: { ...prev[questionId], text: newText, human_edited: true } as Transcript,
        }));
      } catch (err) {
        console.error('Error updating transcript:', err);
        setError('無法更新轉錄');
      } finally {
        setSaving(false);
      }
    },
    [transcripts, user, isSubmissionMode],
  );

  const handleGradingOverride = useCallback(
    async (questionId: string, newMarks: number, newFeedback: string, reason: string) => {
      const draft = gradingDrafts[questionId];
      if (!draft || !assignmentId || !user) return;

      setSaving(true);
      try {
        if (isSubmissionMode) {
          const submission = submissions[selectedSubmissionIndex];
          if (!submission) return;

          // Deep-clone via JSON round-trip so we never mutate the object in state
          const rawGr = submission.grading_result;
          let gr: SubmissionGradingResult;
          try {
            gr = JSON.parse(typeof rawGr === 'string' ? rawGr : JSON.stringify(rawGr));
          } catch (e) {
            console.error('Failed to parse grading_result before override', e);
            setError('批改資料格式異常，無法儲存');
            return;
          }

          const qIndex = gr.questions.findIndex((q) => q.questionId === questionId);
          if (qIndex === -1) {
            setError('找不到對應題目，無法儲存');
            return;
          }

          const existingQ = gr.questions[qIndex];
          if (!existingQ) {
            setError('找不到對應題目，無法儲存');
            return;
          }
          gr.questions[qIndex] = {
            ...existingQ,
            teacher_score: newMarks,
            teacher_feedback: newFeedback,
            reviewed_at: new Date().toISOString(),
            reviewed_by: user.id,
            needs_review: false,
          };

          // Recalculate total, preferring teacher_score when present
          gr.totalMarks = gr.questions.reduce(
            (sum, q) => sum + (q.teacher_score != null ? q.teacher_score : q.marksAwarded),
            0,
          );

          const { error: updateError } = await supabase
            .from('student_submissions')
            .update({ grading_result: gr })
            .eq('id', submission.id);

          if (updateError) throw updateError;

          setSubmissions((prev) => {
            const next = [...prev];
            const current = next[selectedSubmissionIndex];
            if (!current) return prev;
            next[selectedSubmissionIndex] = {
              ...current,
              grading_result: gr,
            };
            return next;
          });
        } else {
          // Legacy: human_overrides + grading_drafts
          const { error: overrideError } = await supabase
            .from('human_overrides')
            .insert({
              assignment_id: assignmentId,
              question_id: questionId,
              paper_map_version: paperMap?.version,
              transcript_version: transcripts[questionId]?.version,
              grading_draft_id: draft.id,
              grader_model_version: 'gpt-4o',
              override_marks: newMarks,
              override_feedback: newFeedback,
              override_type: 'marks',
              reviewed_by: user.id,
              reason,
              previous_values: { marks: draft.marks_awarded, feedback: draft.student_feedback },
            });
          if (overrideError) throw overrideError;

          const { error: updateError } = await supabase
            .from('grading_drafts')
            .update({ marks_awarded: newMarks, student_feedback: newFeedback })
            .eq('id', draft.id);
          if (updateError) throw updateError;
        }

        // Keep local gradingDrafts in sync for both modes
        setGradingDrafts((prev) => ({
          ...prev,
          [questionId]: {
            ...prev[questionId],
            marks_awarded: newMarks,
            student_feedback: newFeedback,
          } as GradingDraft,
        }));

        handleConfirmQuestion(questionId);
      } catch (err) {
        console.error('Error saving override:', err);
        setError('無法保存修改');
      } finally {
        setSaving(false);
      }
    },
    [gradingDrafts, paperMap, transcripts, assignmentId, user, isSubmissionMode, submissions, selectedSubmissionIndex, handleConfirmQuestion],
  );

  const handleApprove = async () => {
    if (!assignmentId) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('assignments')
        .update({ status: 'completed' })
        .eq('id', assignmentId);
      if (error) throw error;
      navigate('/review');
    } catch (err) {
      console.error('Error approving assignment:', err);
      setError('無法完成審核');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!assignment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">找不到作業</Alert>
      </Box>
    );
  }

  const selectedSubmission = isSubmissionMode ? submissions[selectedSubmissionIndex] : null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/review')} sx={{ mr: 2 }}>
          返回列表
        </Button>

        {isSubmissionMode ? (
          <>
            <Typography variant="h5" sx={{ mr: 2 }}>
              審核作業
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200, mr: 'auto' }}>
              <Select
                value={selectedSubmissionIndex}
                onChange={(e) => {
                  // Clear selected question immediately so the right panel doesn't
                  // flash stale content for the previous student before the useEffect fires.
                  setSelectedQuestion(null);
                  setSelectedSubmissionIndex(Number(e.target.value));
                }}
              >
                {submissions.map((s, i) => (
                  <MenuItem key={s.id} value={i}>
                    {s.student_name ?? '未知學生'}
                    {s.student_class ? ` (${s.student_class})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            審核作業 - {displayStudentName}
          </Typography>
        )}

        <Tooltip
          title={
            !allFlaggedReviewed && flaggedQuestions.length > 0
              ? `尚有 ${flaggedQuestions.length - reviewedCount} 題需要覆核`
              : ''
          }
        >
          <span>
            <Button
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={handleApprove}
              disabled={saving}
            >
              完成審核
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Empty state for submission mode when all submissions failed validation */}
      {isSubmissionMode && submissions.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          沒有可審核的學生提交。所有提交可能尚未完成批改，或資料格式異常。
        </Alert>
      )}

      {/* Student info bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Chip
            label={`班級: ${
              (isSubmissionMode ? selectedSubmission?.student_class : assignment.student_class) ?? '-'
            }`}
          />
          {!isSubmissionMode && (
            <Chip label={`年級: ${assignment.grade_level}`} />
          )}
          <Chip label={`題目: ${allQuestions.length}`} color="primary" />
          <Chip
            icon={<FlagIcon />}
            label={`需覆核: ${flaggedQuestions.length}`}
            color={flaggedQuestions.length > 0 ? 'warning' : 'success'}
          />
          {flaggedQuestions.length > 0 && (
            <Chip
              icon={<ApproveIcon />}
              label={`已確認: ${reviewedCount} / ${flaggedQuestions.length}`}
              color={allFlaggedReviewed ? 'success' : 'default'}
              variant="outlined"
            />
          )}
        </Stack>

        {flaggedQuestions.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
              覆核進度
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(reviewedCount / flaggedQuestions.length) * 100}
              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
              color={allFlaggedReviewed ? 'success' : 'primary'}
            />
            <Typography variant="body2" color="text.secondary">
              {Math.round((reviewedCount / flaggedQuestions.length) * 100)}%
            </Typography>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main content */}
      <Grid container spacing={3}>
        {/* Left panel — question list */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1">題目列表</Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showFlaggedOnly}
                    onChange={(e) => setShowFlaggedOnly(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">僅顯示需覆核</Typography>}
              />
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {visibleQuestions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                {showFlaggedOnly ? '所有題目都沒有問題 🎉' : '沒有題目'}
              </Typography>
            )}

            {visibleQuestions.map((questionId) => {
              const flags = questionFlags[questionId] ?? [];
              const hasFlaggedIssues = flags.length > 0;
              const isConfirmed = confirmedQuestions.has(questionId);
              const isSelected = selectedQuestion === questionId;

              return (
                <Button
                  key={questionId}
                  fullWidth
                  variant={isSelected ? 'contained' : 'outlined'}
                  color={isConfirmed ? 'success' : hasFlaggedIssues ? 'warning' : 'primary'}
                  onClick={() => setSelectedQuestion(questionId)}
                  sx={{ mb: 1, justifyContent: 'flex-start', opacity: isConfirmed && !isSelected ? 0.7 : 1 }}
                >
                  {isConfirmed && <ApproveIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                  {questionId}
                  {hasFlaggedIssues && !isConfirmed && (
                    <Chip label={flags.length} size="small" color="warning" sx={{ ml: 'auto' }} />
                  )}
                </Button>
              );
            })}
          </Paper>
        </Grid>

        {/* Right panel — review content */}
        <Grid size={{ xs: 12, md: 9 }}>
          {selectedQuestion && (
            <>
              <Paper sx={{ mb: 2 }}>
                {/* Flag banner */}
                {(() => {
                  const flags = questionFlags[selectedQuestion] ?? [];
                  if (flags.length === 0) return null;
                  return (
                    <Stack spacing={1} sx={{ p: 2, pb: 0 }}>
                      {flags.map((flag: ReviewFlag, i: number) => (
                        <Alert
                          key={i}
                          severity={flag.severity}
                          icon={<WarningIcon />}
                          action={
                            !confirmedQuestions.has(selectedQuestion) ? (
                              <Button
                                color="inherit"
                                size="small"
                                onClick={() => handleConfirmQuestion(selectedQuestion)}
                              >
                                確認無誤
                              </Button>
                            ) : (
                              <Chip label="已確認" size="small" color="success" variant="outlined" />
                            )
                          }
                        >
                          {flag.label}
                        </Alert>
                      ))}
                    </Stack>
                  );
                })()}

                <Tabs
                  value={selectedTab}
                  onChange={(_, v) => setSelectedTab(v)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  {!isSubmissionMode && <Tab label="答案區域" />}
                  {isSubmissionMode && <Tab label="學生作答" />}
                  <Tab label="轉錄內容" />
                  <Tab label="評分結果" />
                  {!isSubmissionMode && <Tab label="驗證問題" />}
                </Tabs>
              </Paper>

              {/* Tab 0: answer image (legacy) or transcribed text (submission) */}
              {selectedTab === 0 && !isSubmissionMode && (
                <PaperMapViewer
                  imageUrls={imageUrls}
                  regions={paperMap?.paper_map_regions || []}
                  selectedQuestionId={selectedQuestion}
                  onRegionSelect={setSelectedQuestion}
                />
              )}

              {selectedTab === 0 && isSubmissionMode && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    學生作答內容
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      pl: 1,
                      borderLeft: '3px solid',
                      borderColor: 'primary.light',
                    }}
                  >
                    {transcripts[selectedQuestion]?.text || '（無作答）'}
                  </Typography>
                </Paper>
              )}

              {/* Tab 1: transcript editor */}
              {selectedTab === 1 && (
                <TranscriptEditor
                  transcript={transcripts[selectedQuestion]}
                  onSave={(text) => handleTranscriptUpdate(selectedQuestion, text)}
                  saving={saving}
                />
              )}

              {/* Tab 2: grading reviewer */}
              {selectedTab === 2 && (
                <GradingReviewer
                  gradingDraft={gradingDrafts[selectedQuestion]}
                  transcript={transcripts[selectedQuestion]?.text}
                  onOverride={(marks, feedback, reason) =>
                    handleGradingOverride(selectedQuestion, marks, feedback, reason)
                  }
                  saving={saving}
                  flags={questionFlags[selectedQuestion]}
                  isConfirmed={confirmedQuestions.has(selectedQuestion)}
                  onConfirm={() => handleConfirmQuestion(selectedQuestion)}
                />
              )}

              {/* Tab 3: verifier flags — legacy only */}
              {selectedTab === 3 && !isSubmissionMode && (
                <VerifierFlagsPanel verification={verifications[selectedQuestion]} />
              )}

              {/* Bottom navigation */}
              {needsReview(selectedQuestion) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    endIcon={<NextIcon />}
                    onClick={() => {
                      const nextUnconfirmed = flaggedQuestions.find(
                        (q) => q !== selectedQuestion && !confirmedQuestions.has(q),
                      );
                      if (nextUnconfirmed) {
                        setSelectedQuestion(nextUnconfirmed);
                        setSelectedTab(2);
                      }
                    }}
                    disabled={
                      !flaggedQuestions.some(
                        (q) => q !== selectedQuestion && !confirmedQuestions.has(q),
                      )
                    }
                  >
                    下一題需覆核
                  </Button>
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
